import React, { useCallback, useEffect } from 'react'
import {
  //   Card,
  Typography,
  Grid,
  Box,
  LinearProgress,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  //   useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material'
import useCustomStyles from '../../../../../utils/hooks/CustomStylesHook'
import {
  API_VERSION_V2,
  DATE_DIRECTIONS,
  getStartAndEndDate,
  GRAPH_PERIODS,
  safeRangeKeys,
  VITAL_KEYS,
} from '../Constant'
import dayjs from 'dayjs'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import {
  getSleepData,
  getSleepTracking,
  getSleepTrackingWeekMonth,
} from '../../../../../services/DeviceServices'
import { useAppSelector } from '../../../../../app/hooks'
import { selectPatientData } from '../../../OpdPatientSlice'
import { selectSafeRanges } from '../DeviceSlice'
import Hypnogram from './Hypnogram'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { capitalizeFirstLetter } from '../../../../patients/utils'

const styles = (theme: any) => ({
  root: {
    padding: theme.spacing(2),
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },
  section: {
    borderRadius: '8px',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  label: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  progressBar: {
    height: 10,
    borderRadius: 5,
    // backgroundColor: '#444',
  },
  chip: {
    fontSize: '0.75rem',
    color: '#fff',
  },
  bar: {
    backgroundColor: '#183f7c',
  },
  toggleGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  toggleBtn: {
    fontSize: '14px',
    fontWeight: 600,
    minWidth: '60px',
  },
  dateDisplay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
  },
})

const SleepTracking: React.FC = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  //   const isMobile = useMediaQuery('(max-width:600px)')
  const safeRanges = useAppSelector(selectSafeRanges)

  const [period, setPeriod] = React.useState(GRAPH_PERIODS.DAY)
  const [currentDate, setCurrentDate] = React.useState(dayjs())
  const [chartData, setChartData] = React.useState<any>({})
  const [sleepChartData, setSleepChartData] = React.useState<any>([])
  const [avgData, setAvgData] = React.useState<any>({})
  const { startDate, endDate } = React.useMemo(() => {
    return getStartAndEndDate(currentDate, period)
  }, [currentDate, period])

  const patient = useAppSelector(selectPatientData)

  const calculateProgress = (duration: string, total: string): number => {
    const parseTime = (timeStr: string): number => {
      const hoursMatch = timeStr?.match(/(\d+)\s*hr/)
      const minsMatch = timeStr?.match(/(\d+)\s*min/)

      const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0
      const mins = minsMatch ? parseInt(minsMatch[1], 10) : 0

      return hours * 60 + mins
    }

    const durationInMinutes = parseTime(duration)
    const totalInMinutes = parseTime(total)

    if (totalInMinutes === 0) return 0

    const progress = (durationInMinutes / totalInMinutes) * 100
    // console.log(Math.min(100, Math.round(progress)))
    return Math.min(100, Math.round(progress))
  }

  const sleepData = [
    {
      label: 'Sleep Duration',
      status: '',
      statusColor: '#d4a017',
      value: `${chartData?.totalSleepHours || ''}`,
      progress: calculateProgress(
        chartData?.totalSleepHours,
        `${safeRanges?.[safeRangeKeys?.SLEEP]?.max} hr 0 min`
      ),
    },
    {
      label: 'Time in Bed',
      status: '',
      statusColor: '#d32f2f',
      value: `${chartData?.totalBedHours || ''}`,
      progress: calculateProgress(
        chartData?.totalBedHours,
        `${safeRanges?.[safeRangeKeys?.SLEEP]?.max} hr 0 min`
      ),
    },
    {
      label: 'Deep Sleep',
      status: '',
      statusColor: '#388e3c',
      value: `${chartData?.deepSleepHours || ''}`,
      progress: calculateProgress(
        chartData?.deepSleepHours,
        `${safeRanges?.[safeRangeKeys?.SLEEP]?.max} hr 0 min`
      ),
    },
    {
      label: 'Light Sleep',
      status: '',
      statusColor: '#388e3c',
      value: `${chartData?.lightSleepHours || ''}`,
      progress: calculateProgress(
        chartData?.lightSleepHours,
        `${safeRanges?.[safeRangeKeys?.SLEEP]?.max} hr 0 min`
      ),
    },
    {
      label: 'Rem Sleep',
      status: '',
      statusColor: '#388e3c',
      value: `${chartData?.remSleepHours || ''}`,
      progress: calculateProgress(
        chartData?.remSleepHours,
        `${safeRanges?.[safeRangeKeys?.SLEEP]?.max} hr 0 min`
      ),
    },
    {
      label: 'Awake Time',
      status: '',
      statusColor: '#d32f2f',
      value: `${chartData?.awakeTimeHours || ''}`,
      progress: calculateProgress(
        chartData?.awakeTimeHours,
        `${safeRanges?.[safeRangeKeys?.SLEEP]?.max} hr 0 min`
      ),
    },
  ]

  const handlePeriodChange = (
    event: React.MouseEvent<HTMLElement>,
    newPeriod: string | null
  ) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod)
    }
  }

  const changeDate = (direction: string) => {
    const amount =
      period === GRAPH_PERIODS?.DAY
        ? 1
        : period === GRAPH_PERIODS?.WEEK
        ? 7
        : 30
    setCurrentDate((prev) =>
      prev.add(direction === DATE_DIRECTIONS?.NEXT ? amount : -amount, 'day')
    )
  }

  const handleGetSleepData = async (ids: string) => {
    const response = await getSleepData(
      ids?.split(',').map((str) => str?.trim())
    )
    if (response?.success) {
      setSleepChartData(response?.data)
    } else {
      setSleepChartData([])
    }
  }

  const handleGetSleepTracking = useCallback(async () => {
    const response = await getSleepTracking(
      patient?.muhId,
      currentDate.format('YYYY-MM-DD'),
      VITAL_KEYS?.SLEEP_TRACKING,
      API_VERSION_V2
    )
    if (response?.success) {
      setChartData(response?.data)
      handleGetSleepData(response?.data?.rawRecordIds)
    }
  }, [patient?.muhId, currentDate])

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload?.length) {
      return (
        <Box
          bgcolor='#222'
          padding='8px 12px'
          border='1px solid #444'
          borderRadius={4}
          color='#fff'
        >
          <Box component='p' margin={0} mb={2} fontWeight={600}>
            {label}
          </Box>
          {payload?.map((entry: any, index: number) => (
            <Box
              key={index}
              display='flex'
              alignItems='center'
              margin={0}
              mb={0.5}
              color='#fff'
            >
              <Box
                component='span'
                width={12}
                height={12}
                borderRadius='50%'
                bgcolor={entry?.color}
                display='inline-block'
                marginRight={1}
              />
              <Box component='span' color={entry?.color}>
                {capitalizeFirstLetter(entry?.name)}: {entry?.value} hrs
              </Box>
            </Box>
          ))}
        </Box>
      )
    }
    return null
  }

  const parseSleepDuration = (str: string) => {
    const hrMatch = str.match(/(\d+)\s*hr/)
    const minMatch = str.match(/(\d+)\s*min/)

    const hours = hrMatch ? parseInt(hrMatch?.[1]) : 0
    const minutes = minMatch ? parseInt(minMatch?.[1]) : 0

    return +(hours + minutes / 60).toFixed(2)
  }

  const handleGetSleepTrackingWeekMonth = useCallback(async () => {
    const response = await getSleepTrackingWeekMonth(
      patient?.muhId,
      startDate.format('YYYY-MM-DD'),
      endDate.format('YYYY-MM-DD')
    )
    if (response?.success) {
      const processedChartData = response?.data?.dailyStats?.map(
        (item: any) => ({
          date: item.date,
          awakeTimeHours: parseSleepDuration(
            item?.awakeTimeHours || '0 hr 0 min'
          ),
          remSleepHours: parseSleepDuration(
            item?.remSleepHours || '0 hr 0 min'
          ),
          lightSleepHours: parseSleepDuration(
            item?.lightSleepHours || '0 hr 0 min'
          ),
          deepSleepHours: parseSleepDuration(
            item?.deepSleepHours || '0 hr 0 min'
          ),
          ...response?.data?.dailyStats,
        })
      )
      setChartData(processedChartData)
      setAvgData(response?.data?.average)
    }
  }, [patient?.muhId, startDate, endDate])

  useEffect(() => {
    if (period === GRAPH_PERIODS?.DAY) {
      handleGetSleepTracking()
    } else {
      handleGetSleepTrackingWeekMonth()
    }
  }, [handleGetSleepTracking, handleGetSleepTrackingWeekMonth, period])

  return (
    <Box className={classes?.root}>
      {/* <Typography variant={isMobile ? 'h5' : 'h4'} className={classes?.header}>
        Sleep
      </Typography> */}
      <Box className={classes?.toggleGroup}>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={handlePeriodChange}
          color='primary'
          size='small'
        >
          <ToggleButton value='day' className={classes?.toggleBtn}>
            D
          </ToggleButton>
          <ToggleButton value='week' className={classes?.toggleBtn}>
            W
          </ToggleButton>
          <ToggleButton value='month' className={classes?.toggleBtn}>
            M
          </ToggleButton>
        </ToggleButtonGroup>
        <Box className={classes?.dateDisplay}>
          <IconButton
            onClick={() => changeDate(DATE_DIRECTIONS?.PREVIOUS)}
            color='inherit'
          >
            <ChevronLeft />
          </IconButton>
          <Typography>
            {currentDate?.format(
              period === GRAPH_PERIODS?.DAY
                ? 'MMM DD, YYYY'
                : period === GRAPH_PERIODS?.WEEK
                ? '[Week of] MMM DD, YYYY'
                : 'MMM YYYY'
            )}
          </Typography>
          <IconButton
            onClick={() => changeDate(DATE_DIRECTIONS?.NEXT)}
            color='inherit'
            disabled={
              currentDate.isSame(dayjs(), 'day') ||
              currentDate.isAfter(dayjs(), 'day')
            }
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      {period === GRAPH_PERIODS?.DAY ? (
        <>
          <Box>
            <Hypnogram data={sleepChartData} />
          </Box>

          <Box
            display={'flex'}
            justifyContent={'center'}
            fontSize={'13px'}
            fontWeight={500}
          >
            {chartData?.mainStartDateTime
              ? dayjs(chartData?.mainStartDateTime).format(
                  'MMMM D, YYYY h:mm A'
                )
              : '-'}
            {' - '}
            {chartData?.mainEndDateTime
              ? dayjs(chartData?.mainEndDateTime).format('MMMM D, YYYY h:mm A')
              : '-'}
          </Box>

          <Box className={classes?.section}>
            <Typography variant='h6' pb={2}>
              Sleep Overview
            </Typography>
            <Grid container spacing={2}>
              {sleepData?.map((item, index) => (
                <Grid item xs={12} key={index}>
                  <Box className={classes?.label}>
                    <Typography>{item?.label}</Typography>
                    {item?.status && (
                      <Chip
                        label={item?.status}
                        size='small'
                        style={{ backgroundColor: item?.statusColor }}
                        className={classes?.chip}
                      />
                    )}
                  </Box>
                  <LinearProgress
                    variant='determinate'
                    value={item.progress}
                    classes={{ root: classes?.progressBar, bar: classes?.bar }}
                  />
                  <Typography align='right' style={{ marginTop: 4 }}>
                    {item?.value}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        </>
      ) : (
        <>
          {chartData?.length > 0 && (
            <>
              <Box bgcolor='#121212' padding={2} borderRadius={2}>
                <Typography
                  fontSize={'13px'}
                  fontWeight={600}
                  ml={5}
                  mb={0.5}
                  color={'#FFFFFF'}
                >
                  hrs
                </Typography>
                <ResponsiveContainer width='100%' height={300}>
                  <BarChart
                    data={chartData}
                    barSize={6}
                    margin={{ top: 20, bottom: 20 }}
                  >
                    <CartesianGrid stroke='#333333' />
                    <XAxis
                      dataKey='date'
                      fontSize={10}
                      interval={0}
                      angle={-45}
                      textAnchor='end'
                      tick={{ fill: '#FFFFFF', fontSize: 10 }}
                      axisLine={{ stroke: '#AAAAAA' }}
                      tickLine={{ stroke: '#AAAAAA' }}
                    />
                    <YAxis
                      fontSize={12}
                      tick={{ fill: '#FFFFFF' }}
                      axisLine={{ stroke: '#AAAAAA' }}
                      tickLine={{ stroke: '#AAAAAA' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {/* <Legend wrapperStyle={{ color: '#FFFFFF' }} /> */}
                    <Bar dataKey='deepSleepHours' stackId='a' fill='#7347F6' />
                    <Bar dataKey='lightSleepHours' stackId='a' fill='#B38EF8' />
                    <Bar dataKey='remSleepHours' stackId='a' fill='#D9C7F4' />
                    <Bar
                      dataKey='awakeTimeHours'
                      stackId='a'
                      fill='#FFECDC'
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              <Box
                display='flex'
                flexWrap='wrap'
                justifyContent='center'
                gap={2}
                mt={1.5}
                ml={4}
              >
                <Box
                  width={250}
                  textAlign='center'
                  border='1px solid grey'
                  borderRadius='20px'
                  p={2}
                >
                  <Typography variant='h6'>
                    {avgData?.avgTotalSleepHours}
                  </Typography>
                  <Typography variant='subtitle2' fontWeight={600}>
                    Average Total Sleep Hours
                  </Typography>
                </Box>

                <Box
                  width={250}
                  textAlign='center'
                  border='1px solid grey'
                  borderRadius='20px'
                  p={2}
                >
                  <Typography variant='h6'>
                    {avgData?.avgTotalBedHours}
                  </Typography>
                  <Typography variant='subtitle2' fontWeight={600}>
                    Average Total Bed Hours
                  </Typography>
                </Box>

                <Box
                  width={250}
                  textAlign='center'
                  border='1px solid grey'
                  borderRadius='20px'
                  p={2}
                >
                  <Typography variant='h6'>
                    {avgData?.avgAwakeTimeHours}
                  </Typography>
                  <Typography variant='subtitle2' fontWeight={600}>
                    Average Awake Time
                  </Typography>
                </Box>

                <Box
                  width={250}
                  textAlign='center'
                  border='1px solid grey'
                  borderRadius='20px'
                  p={2}
                >
                  <Typography variant='h6'>
                    {avgData?.avgRemSleepHours}
                  </Typography>
                  <Typography variant='subtitle2' fontWeight={600}>
                    Average REM Sleep
                  </Typography>
                </Box>

                <Box
                  width={250}
                  textAlign='center'
                  border='1px solid grey'
                  borderRadius='20px'
                  p={2}
                >
                  <Typography variant='h6'>
                    {avgData?.avgLightSleepHours}
                  </Typography>
                  <Typography variant='subtitle2' fontWeight={600}>
                    Average Light Sleep
                  </Typography>
                </Box>

                <Box
                  width={250}
                  textAlign='center'
                  border='1px solid grey'
                  borderRadius='20px'
                  p={2}
                >
                  <Typography variant='h6'>
                    {avgData?.avgDeepSleepHours}
                  </Typography>
                  <Typography variant='subtitle2' fontWeight={600}>
                    Average Deep Sleep
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  )
}

export default SleepTracking
